"use client"

import { useMemo, useState } from "react"

import { CalculatorCard } from "@/components/calculators/calculator-card"
import { CalculatorResult } from "@/components/calculators/calculator-result"
import { KuhlykTip } from "@/components/kuhlyk/kyhlyk-tip"

const MIN_WATER_LITERS = 0.1
const MAX_WATER_LITERS = 20
const MIN_GRAMS_PER_LITER = 0
const MAX_GRAMS_PER_LITER = 500

function roundToOne(value: number) {
  return Math.round(value * 10) / 10
}

function sanitizeNumber(
  value: number,
  min: number,
  max: number,
  fallback: number
) {
  if (!Number.isFinite(value)) {
    return fallback
  }

  return Math.min(max, Math.max(min, value))
}

export function SaltSugarCalculator() {
  const [waterLiters, setWaterLiters] = useState<number>(1)
  const [saltPerLiter, setSaltPerLiter] = useState<number>(40)
  const [sugarPerLiter, setSugarPerLiter] = useState<number>(60)

  const safeWaterLiters = sanitizeNumber(
    waterLiters,
    MIN_WATER_LITERS,
    MAX_WATER_LITERS,
    1
  )
  const safeSaltPerLiter = sanitizeNumber(
    saltPerLiter,
    MIN_GRAMS_PER_LITER,
    MAX_GRAMS_PER_LITER,
    40
  )
  const safeSugarPerLiter = sanitizeNumber(
    sugarPerLiter,
    MIN_GRAMS_PER_LITER,
    MAX_GRAMS_PER_LITER,
    60
  )

  const result = useMemo(() => {
    return {
      salt: roundToOne(safeWaterLiters * safeSaltPerLiter),
      sugar: roundToOne(safeWaterLiters * safeSugarPerLiter),
    }
  }, [safeSaltPerLiter, safeSugarPerLiter, safeWaterLiters])

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <CalculatorCard
        title="Калькулятор солі та цукру"
        description="Швидкий підрахунок пропорцій для будь-якого об’єму рідини. Зручно, коли базовий рецепт уже є, але потрібно масштабувати партію без ручних підрахунків."
      >
        <div className="grid gap-5">
          <label className="block" htmlFor="water-liters">
            <span className="mb-2 block text-sm font-medium text-neutral-800">
              Об’єм рідини (л)
            </span>

            <input
              id="water-liters"
              type="number"
              min={MIN_WATER_LITERS}
              max={MAX_WATER_LITERS}
              step={0.1}
              inputMode="decimal"
              value={waterLiters}
              onChange={(e) => setWaterLiters(Number(e.target.value))}
              className="w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition focus:border-emerald-400"
            />
          </label>

          <label className="block" htmlFor="salt-per-liter">
            <span className="mb-2 block text-sm font-medium text-neutral-800">
              Сіль на 1 л (г)
            </span>

            <input
              id="salt-per-liter"
              type="number"
              min={MIN_GRAMS_PER_LITER}
              max={MAX_GRAMS_PER_LITER}
              step={1}
              inputMode="numeric"
              value={saltPerLiter}
              onChange={(e) => setSaltPerLiter(Number(e.target.value))}
              className="w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition focus:border-emerald-400"
            />
          </label>

          <label className="block" htmlFor="sugar-per-liter">
            <span className="mb-2 block text-sm font-medium text-neutral-800">
              Цукор на 1 л (г)
            </span>

            <input
              id="sugar-per-liter"
              type="number"
              min={MIN_GRAMS_PER_LITER}
              max={MAX_GRAMS_PER_LITER}
              step={1}
              inputMode="numeric"
              value={sugarPerLiter}
              onChange={(e) => setSugarPerLiter(Number(e.target.value))}
              className="w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition focus:border-emerald-400"
            />
          </label>
        </div>
      </CalculatorCard>

      <div className="space-y-5">
        <CalculatorResult
          title="Розрахунок пропорцій"
          items={[
            {
              label: "Сіль",
              value: `${result.salt} г`,
              hint: `${safeSaltPerLiter} г на 1 л`,
            },
            {
              label: "Цукор",
              value: `${result.sugar} г`,
              hint: `${safeSugarPerLiter} г на 1 л`,
            },
          ]}
          note="Калькулятор зручний для масштабування готового рецепта. Якщо рецепт критичний до безпеки зберігання, не змінюйте пропорції навмання."
        />

        <KuhlykTip
          variant="warm"
          title="Порада Кухлика"
          text="Для ферментації сіль краще рахувати особливо уважно й по можливості зважувати на кухонних вагах, а не відміряти ложками."
        />
      </div>
    </div>
  )
}